import { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { Users, Loader2, Eye, Mail } from "lucide-react";
import { adminWeeklyRecipients, adminWeeklyPreview } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

/**
 * Admin-only spot-check for Sunday's weekly-recap batch. Shows every user
 * who WILL receive an email (i.e. `email_weekly_digest=true` AND has activity
 * to recap) with the subject line, and lets admin preview any recap in a
 * DOMPurify-sanitised dialog before hitting the send button.
 *
 * Sits alongside AdminSalesCard on the dashboard. Renders nothing for
 * non-admins.
 */
export const AdminWeeklyRecapCard = ({ role }) => {
  const [rows, setRows] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [activeEmail, setActiveEmail] = useState("");

  useEffect(() => {
    if (role !== "admin") return;
    adminWeeklyRecipients(50)
      .then((r) => setRows(r.recipients || []))
      .catch(() => setRows([]));
  }, [role]);

  const openPreview = async (email) => {
    setActiveEmail(email);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreview(null);
    try {
      const p = await adminWeeklyPreview(email);
      setPreview(p);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't build preview");
      setPreview({ empty: true, reason: "Preview failed." });
    } finally {
      setPreviewLoading(false);
    }
  };

  const sanitizedHtml = useMemo(
    () => (preview?.html ? DOMPurify.sanitize(preview.html, {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ["style", "script", "iframe", "object", "embed"],
      FORBID_ATTR: ["style", "onerror", "onload", "onclick"],
    }) : ""),
    [preview?.html]
  );

  if (role !== "admin") return null;

  return (
    <div data-testid="admin-weekly-recap-card" className="rounded-2xl border border-border bg-card p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <Users className="h-5 w-5 text-accent" />
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Admin · editorial</span>
      </div>
      <h3 className="font-serif text-2xl font-bold">Weekly recap — recipient spot-check</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Preview any user's Friday email before you fire the batch. Only users with activity this week appear here.
      </p>

      {rows === null && (
        <div className="mt-5 space-y-2 animate-pulse">
          {[0, 1, 2].map((i) => <div key={i} className="h-14 rounded-xl bg-secondary/60" />)}
        </div>
      )}

      {rows && rows.length === 0 && (
        <p className="mt-5 text-sm text-muted-foreground">Nobody has enough activity to warrant a recap right now.</p>
      )}

      {rows && rows.length > 0 && (
        <>
          <div className="mt-4 flex items-center gap-2 text-xs">
            <span className="rounded-full bg-primary/10 text-primary font-bold uppercase tracking-wider px-2 py-0.5">
              {rows.length} recipient{rows.length === 1 ? "" : "s"} ready
            </span>
          </div>
          <ul className="mt-4 divide-y divide-border rounded-xl border border-border overflow-hidden max-h-[380px] overflow-y-auto" data-testid="admin-recap-list">
            {rows.map((r) => (
              <li
                key={r.user_id}
                data-testid={`admin-recap-row-${r.user_id}`}
                className="flex items-center gap-3 px-4 py-3 text-sm hover:bg-secondary/40 transition"
              >
                <span className="grid place-items-center h-9 w-9 rounded-lg bg-accent/10 text-accent shrink-0">
                  <Mail className="h-4 w-4" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{r.name || r.email}</p>
                  <p className="text-xs text-muted-foreground truncate">{r.subject}</p>
                </div>
                <button
                  onClick={() => openPreview(r.email)}
                  data-testid={`admin-recap-preview-${r.user_id}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:border-accent/50 hover:text-accent transition"
                >
                  <Eye className="h-3.5 w-3.5" /> Preview
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <Dialog open={previewOpen} onOpenChange={(o) => !o && setPreviewOpen(false)}>
        <DialogContent className="max-w-lg p-0 overflow-hidden max-h-[85vh] overflow-y-auto">
          <DialogTitle className="sr-only">Weekly recap preview for {activeEmail}</DialogTitle>
          <DialogDescription className="sr-only">Preview of the weekly recap email for {activeEmail}.</DialogDescription>
          <div className="p-6 pb-3">
            <h2 className="font-serif text-2xl font-bold">Preview · {activeEmail}</h2>
            <p className="mt-1 text-sm text-muted-foreground">This is what would land in their inbox.</p>
          </div>
          <div className="mx-6 mb-6 rounded-xl border border-border bg-secondary/30 overflow-hidden">
            {previewLoading ? (
              <div className="p-12 grid place-items-center">
                <Loader2 className="h-6 w-6 animate-spin text-accent" />
              </div>
            ) : preview?.empty ? (
              <div className="p-8 text-center text-sm text-muted-foreground">{preview?.reason}</div>
            ) : (
              <>
                <div className="px-5 py-3 border-b border-border text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">
                  Subject: <span className="normal-case tracking-normal text-foreground">{preview?.subject}</span>
                </div>
                <div
                  className="p-4 max-h-[50vh] overflow-y-auto"
                  data-testid="admin-recap-preview-html"
                  dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                />
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
